import { getChartLabel } from "./chartLabel";
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  LineChart,
  LabelList,
} from "recharts";
import { useState } from "react";
import { Form } from "react-bootstrap";
import CustomLegend from "./CustomLegend";
import CustomTooltip from "./CustomTooltip";

//kpi-9
const PlhivChart = ({ data, filters, selectedState }) => {
  const [showTable, setShowTable] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "place", // default sort by place
    direction: "asc",
  });

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          backgroundColor: "#fff",
          border: "1px solid #ddd",
          borderRadius: "8px",
          marginTop: "20px",
        }}
      >
        <h5 style={{ color: "#666", fontSize: "20px" }}>No data available</h5>
      </div>
    );
  }
  const plhivData = data.map((item) => {
    return {
      place: item.Place,
      estimated_plhivs: Number(item.estimated_no_of_plhivs ?? 0),
      plhiv_already_art: Number(item.total_plhiv_already_on_art ?? 0),
      total_test_conducted: Number(item.total_viral_test_load_conducted ?? 0),
      virally_suppressed: Number(item.virally_supressed ?? 0),
      percentage_vl_tested: Number(item.percentage_of_VL_tested ?? 0),
      percentage_vl_suppressed: Number(item.percentage_supressed ?? 0),
    };
  });

  // Sorting logic
  const sortedData = [...plhivData].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Handle sorting click
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      } else {
        return { key, direction: "asc" };
      }
    });
  };

  // Icon display function
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "⬍"; // default icon
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const chunkArray = (array, chunkCount) => {
    const result = [];
    const chunkSize = Math.ceil(array.length / chunkCount);

    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize));
    }

    return result;
  };

  const getChartCount = (length) => {
   if (length < 9) return 1;
    if (length >= 9 && length <= 18) return 2;
    if (length <= 45) return 3;
    return Math.ceil(length / 20);
  };

  const chartCount = getChartCount(plhivData.length);
  const chunkedData = chunkArray(plhivData, chartCount);

  return (
    <div style={{ width: "100%", position: "relative" }}>
      <div className="card hptCard mt-4 border-0">
        <div class="card-header brBtomlr">
          <div class="headnumText justify-content-between">
            <div class="cardTitle">
              <h5>
                VL Test Services (GC) {getChartLabel(filters, selectedState)}
              </h5>
            </div>

            <div class="cardDropDown gap-3">
              <div style={{ marginRight: "10px", fontWeight: 500 }}>
                {showTable ? "Chart View" : "Table View"}
              </div>
              <Form>
                <Form.Check
                  type="switch"
                  id="chart-table-toggle"
                  onChange={() => setShowTable((prev) => !prev)}
                  checked={showTable}
                />
              </Form>
            </div>
          </div>
        </div>
      </div>
      {showTable ? (
        // Table View
        <div className="card hptCard mt-4 border-0">
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="thead-light">
                <tr>
                  <th className=" fw-bold ">Place</th>
                  <th
                    className="text-center fw-bold cursor-pointer"
                    onClick={() => handleSort("estimated_plhivs")}
                  >
                    Estimated PLHIV {getSortIcon("estimated_plhivs")}
                  </th>
                  <th
                    className="text-center fw-bold cursor-pointer"
                    onClick={() => handleSort("plhiv_already_art")}
                  >
                    PLHIV already on ART {getSortIcon("plhiv_already_art")}
                  </th>
                  <th
                    className="text-center fw-bold cursor-pointer"
                    onClick={() => handleSort("total_test_conducted")}
                  >
                    Total viral Load test conducted{" "}
                    {getSortIcon("total_test_conducted")}
                  </th>
                  <th
                    className="text-center fw-bold cursor-pointer"
                    onClick={() => handleSort("virally_suppressed")}
                  >
                    Virally suppressed {getSortIcon("virally_suppressed")}
                  </th>
                  <th
                    className="text-center fw-bold cursor-pointer"
                    onClick={() => handleSort("percentage_vl_tested")}
                  >
                    Percentage of VL Tested{" "}
                    {getSortIcon("percentage_vl_tested")}
                  </th>
                  <th
                    className="text-center fw-bold cursor-pointer"
                    onClick={() => handleSort("percentage_vl_suppressed")}
                  >
                    Percentage of Virally Suppressed{" "}
                    {getSortIcon("percentage_vl_suppressed")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ fontWeight: "bold" }}>{item.place}</td>
                    <td>{item.estimated_plhivs}</td>
                    <td>{item.plhiv_already_art}</td>
                    <td>{item.total_test_conducted}</td>
                    <td>{item.virally_suppressed}</td>
                    <td>{item.percentage_vl_tested}%</td>
                    <td>{item.percentage_vl_suppressed}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        // Chart View
        <div>
          {chunkedData.map((chunk, index) => (
            <div key={`bar-${index}`} className="card hptCard mt-4 border-0">
              <div className="card-body">
                <h6 style={{ fontWeight: "bold" }}>VL Test Services (GC)</h6>
              </div>
              <ResponsiveContainer width="100%" height={500}>
                <BarChart
                  width={500}
                  height={500}
                  data={chunk}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 130,
                  }}
                >
                  {/* <CartesianGrid strokeDasharray="3 3" /> */}
                  <XAxis
                    dataKey="place"
                    angle={-90}
                    textAnchor="end"
                    interval={0}
                    label={{
                      value: "",
                      position: "insideBottom",
                      offset: -110,
                      color: "#ca8282ff",
                      fontWeight: "bold",
                    }}
                  />
                  <YAxis
                    yAxisId="left"
                    orientation="left"
                    label={{
                      value: "",
                      angle: -90,
                      position: "outside",
                      offset: -100,
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="top"
                    content={
                      <CustomLegend
                        order={[
                          "Estimated PLHIV",
                          "PLHIV already on ART",
                          "Total VL Test Conducted",
                          "Virally Suppressed",
                        ]}
                      />
                    }
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="estimated_plhivs"
                    fill="#00a084"
                    name="Estimated PLHIV"
                  >
                    <LabelList
                      dataKey="estimated_plhivs"
                      position="top"
                      content={({ x, y, width, value }) => {
                        if (value == null) return null;
                        return (
                          <text
                            x={x + width / 2} // center horizontally
                            y={y - 5} // position slightly above bar
                            fontSize={12}
                            fontWeight="bold"
                            textAnchor="start" // 👈 could also try "middle" for alignment
                            transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                  <Bar
                    yAxisId="left"
                    dataKey="plhiv_already_art"
                    fill="#ffc40c"
                    name="PLHIV already on ART"
                  >
                    <LabelList
                      dataKey="plhiv_already_art"
                      position="top"
                      content={({ x, y, width, value }) => {
                        if (value == null) return null;
                        return (
                          <text
                            x={x + width / 2} // center horizontally
                            y={y - 5} // position slightly above bar
                            fontSize={12}
                            fontWeight="bold"
                            textAnchor="start" // 👈 could also try "middle" for alignment
                            transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                  <Bar
                    yAxisId="left"
                    dataKey="total_test_conducted"
                    fill="#dd1e47"
                    name="Total VL Test Conducted"
                  >
                    <LabelList
                      dataKey="total_test_conducted"
                      position="top"
                      content={({ x, y, width, value }) => {
                        if (value == null) return null;
                        return (
                          <text
                            x={x + width / 2} // center horizontally
                            y={y - 5} // position slightly above bar
                            fontSize={12}
                            fontWeight="bold"
                            textAnchor="start" // 👈 could also try "middle" for alignment
                            transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>

                  <Bar
                    yAxisId="left"
                    dataKey="virally_suppressed"
                    fill="#608ce6ff"
                    name="Virally Suppressed"
                  >
                    <LabelList
                      dataKey="virally_suppressed"
                      position="top"
                      content={({ x, y, width, value }) => {
                        if (value == null) return null;
                        return (
                          <text
                            x={x + width / 2} // center horizontally
                            y={y - 5} // position slightly above bar
                            fontSize={12}
                            fontWeight="bold"
                            textAnchor="start" // 👈 could also try "middle" for alignment
                            transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ))}

          {/* 🔹 LINE CHARTS */}
          {chunkedData.map((chunk, index) => (
            <div key={`line-${index}`} className="card hptCard mt-4 border-0">
              <div className="card-body">
                <h6 style={{ fontWeight: "bold" }}>
                  Percentage of VL Tested and Suppressed
                </h6>
              </div>

              <br />
              <ResponsiveContainer width="100%" height={500}>
                <LineChart
                  width={500}
                  height={500}
                  data={chunk}
                  margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 130,
                  }}
                >
                  {/* <CartesianGrid strokeDasharray="3 3" /> */}
                  <XAxis
                    dataKey="place"
                    angle={-90}
                    textAnchor="end"
                    interval={0}
                    label={{
                      value: "",
                      position: "insideBottom",
                      offset: -110,
                      color: "#ca8282ff",
                      fontWeight: "bold",
                    }}
                  />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="top"
                    content={
                      <CustomLegend
                        order={[
                          "Percentage of VL Tested",
                          "Percentageof Virally Suppressed",
                        ]}
                      />
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="percentage_vl_tested"
                    stroke="#00a084"
                    fill="#00a084"
                    name="Percentage of VL Tested"
                  />
                  <Line
                    type="monotone"
                    dataKey="percentage_vl_suppressed"
                    stroke="#dd1e47"
                    fill="#dd1e47"
                    name="Percentage of Virally Suppressed"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default PlhivChart;
